import React, { useState, useEffect } from "react";
import moment from "moment";
import "../style.scss";

const SensorLogTimeline = ({ sensorId, limit = 20 }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    actie_type: 'all',
    timeframe: '24'
  });
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [sensorId, filters]);

  const fetchLogs = async (loadMore = false) => {
    let url;
    try {
      setLoading(true);
      const skip = loadMore ? logs.length : 0;
      
      url = `/api/admin/sensor/logs/${sensorId}?limit=${limit}&skip=${skip}`;
      
      // Add filters to URL
      if (filters.status !== 'all') {
        url += `&status=${filters.status}`;
      }
      if (filters.actie_type !== 'all') {
        url += `&actie_type=${filters.actie_type}`;
      }
      if (filters.timeframe !== 'all') {
        const hours = parseInt(filters.timeframe);
        const since = new Date(Date.now() - (hours * 60 * 60 * 1000)).toISOString();
        url += `&since=${since}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Ensure results is an array
      const results = Array.isArray(data.results) ? data.results : [];
      
      if (loadMore) {
        setLogs(prev => [...prev, ...results]);
      } else {
        setLogs(results);
      }
      
      setHasMore(data.pagination?.hasMore || false);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const createTestLogs = async () => {
    try {
      const response = await fetch(`/api/admin/sensor/logs/${sensorId}/create-test`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Herlaad de logs na het maken van test data
        fetchLogs();
      }
    } catch (error) {
      // Fout bij maken test logs - stilletjes negeren
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <i className="fas fa-check-circle text-success"></i>;
      case 'error':
        return <i className="fas fa-exclamation-circle text-danger"></i>;
      case 'warning':
        return <i className="fas fa-exclamation-triangle text-warning"></i>;
      case 'pending':
        return <i className="fas fa-clock text-info"></i>;
      default:
        return <i className="fas fa-info-circle text-secondary"></i>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'danger';
      case 'warning': return 'warning';
      case 'pending': return 'info';
      default: return 'secondary';
    }
  };

  const getActionIcon = (actieType) => {
    if (actieType.includes('schakel')) return 'fas fa-power-off';
    if (actieType.includes('ampere')) return 'fas fa-bolt';
    if (actieType.includes('update') || actieType.includes('instellingen')) return 'fas fa-cog';
    if (actieType.includes('process')) return 'fas fa-play';
    if (actieType.includes('data')) return 'fas fa-database';
    if (actieType.includes('alert')) return 'fas fa-bell';
    if (actieType.includes('monitoring')) return 'fas fa-eye';
    return 'fas fa-info';
  };

  const formatDuration = (ms) => {
    if (!ms) return null;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const groupLogsByDate = (logs) => {
    const groups = {};
    logs.forEach(log => {
      const date = moment(log.datum).format('YYYY-MM-DD');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(log);
    });
    return groups;
  };

  if (loading && logs.length === 0) {
    return (
      <div className="sensor-log-timeline">
        <div className="d-flex justify-content-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sensor-log-timeline">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Fout bij laden van logs: {error}
        </div>
      </div>
    );
  }

  const groupedLogs = groupLogsByDate(logs);

  return (
    <div className="sensor-log-timeline">
      {/* Filter controls */}
      <div className="timeline-filters mb-4">
        <div className="row">
          <div className="col-md-3">
            <label className="form-label">Status</label>
            <select 
              className="form-select form-select-sm"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">Alle status</option>
              <option value="success">Succesvol</option>
              <option value="error">Fouten</option>
              <option value="warning">Waarschuwingen</option>
              <option value="pending">In behandeling</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Actie Type</label>
            <select 
              className="form-select form-select-sm"
              value={filters.actie_type}
              onChange={(e) => handleFilterChange('actie_type', e.target.value)}
            >
              <option value="all">Alle acties</option>
              <option value="action_schakel_in">Inschakelen</option>
              <option value="action_schakel_uit">Uitschakelen</option>
              <option value="action_ampere">Ampere wijziging</option>
              <option value="update_instellingen">Instellingen update</option>
              <option value="data_received">Data ontvangen</option>
              <option value="process_actions">Actie verwerking</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Periode</label>
            <select 
              className="form-select form-select-sm"
              value={filters.timeframe}
              onChange={(e) => handleFilterChange('timeframe', e.target.value)}
            >
              <option value="all">Alle tijd</option>
              <option value="1">Laatste uur</option>
              <option value="24">Laatste 24 uur</option>
              <option value="168">Laatste week</option>
              <option value="720">Laatste maand</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">&nbsp;</label>
            <div className="d-grid gap-1">
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={() => fetchLogs()}
                disabled={loading}
              >
                <i className="fas fa-sync-alt me-1"></i>
                Vernieuwen
              </button>
              <button 
                className="btn btn-outline-success btn-sm"
                onClick={createTestLogs}
                disabled={loading}
              >
                <i className="fas fa-plus me-1"></i>
                Test Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="timeline-container">
        {Object.keys(groupedLogs).length === 0 ? (
          <div className="text-center p-4">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">Geen logs gevonden</h5>
            <p className="text-muted">Er zijn geen logs voor de geselecteerde filters.</p>
          </div>
        ) : (
          Object.keys(groupedLogs).map(date => (
            <div key={date} className="timeline-day">
              <div className="timeline-date-header">
                <h6 className="text-muted mb-0">
                  <i className="fas fa-calendar-day me-2"></i>
                  {moment(date).format('dddd DD MMMM YYYY')}
                </h6>
              </div>
              
              <div className="timeline-items">
                {groupedLogs[date].map((log, index) => (
                  <div key={log._id} className="timeline-item">
                    <div className="timeline-marker">
                      <div className={`timeline-marker-icon bg-${getStatusColor(log.status)}`}>
                        {getStatusIcon(log.status)}
                      </div>
                    </div>
                    
                    <div className="timeline-content">
                      <div className="timeline-card">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="timeline-header">
                              <div className="d-flex align-items-center mb-1">
                                <i className={`${getActionIcon(log.actie_type)} text-primary me-2`}></i>
                                <h6 className="mb-0 timeline-title">{log.beschrijving}</h6>
                              </div>
                              <small className="text-muted">
                                <i className="fas fa-clock me-1"></i>
                                {moment(log.datum).format('HH:mm:ss')}
                                {log.verwerktijd_ms && (
                                  <span className="ms-2">
                                    <i className="fas fa-stopwatch me-1"></i>
                                    {formatDuration(log.verwerktijd_ms)}
                                  </span>
                                )}
                              </small>
                            </div>
                            <span className={`badge bg-${getStatusColor(log.status)} badge-sm`}>
                              {log.status}
                            </span>
                          </div>

                          {/* Error message */}
                          {log.error_message && (
                            <div className="alert alert-danger alert-sm mb-2">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              <strong>Fout:</strong> {log.error_message}
                            </div>
                          )}

                          {/* Additional data */}
                          {log.data && Object.keys(log.data).length > 0 && (
                            <div className="timeline-details">
                              <button 
                                className="btn btn-link btn-sm p-0"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#details-${log._id}`}
                              >
                                <i className="fas fa-chevron-down me-1"></i>
                                Details tonen
                              </button>
                              <div className="collapse" id={`details-${log._id}`}>
                                <div className="timeline-data mt-2">
                                  {log.data.execution_time_ms && (
                                    <div className="mb-1">
                                      <strong>Uitvoeringstijd:</strong> {formatDuration(log.data.execution_time_ms)}
                                    </div>
                                  )}
                                  {log.data.payload_sent && (
                                    <div className="mb-1">
                                      <strong>Verzonden:</strong> 
                                      <code className="ms-1">{log.data.payload_sent}</code>
                                    </div>
                                  )}
                                  {log.data.payload_received && (
                                    <div className="mb-1">
                                      <strong>Ontvangen:</strong> 
                                      <code className="ms-1">{log.data.payload_received}</code>
                                    </div>
                                  )}
                                  {log.data.update_details && (
                                    <div className="mb-1">
                                      <strong>Update:</strong> 
                                      <span className="ms-1">{JSON.stringify(log.data.update_details)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* User info */}
                          {log.user_id && (
                            <div className="timeline-user">
                              <small className="text-muted">
                                <i className="fas fa-user me-1"></i>
                                {log.user_id.naam || log.user_id.email}
                              </small>
                            </div>
                          )}

                          {/* Tags */}
                          {log.tags && log.tags.length > 0 && (
                            <div className="timeline-tags mt-2">
                              {log.tags.map(tag => (
                                <span key={tag} className="badge bg-light text-dark me-1">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center mt-4">
          <button 
            className="btn btn-outline-primary"
            onClick={() => fetchLogs(true)}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Laden...
              </>
            ) : (
              <>
                <i className="fas fa-chevron-down me-2"></i>
                Meer logs laden
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default SensorLogTimeline;